import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../../../../../../../redux/hooks';
import useGroupDetails from '../../../../../../../../queries/useGroupDetails';
import { API } from '../../../../../../../../api';
import { useProgramContext } from '../../../../../../../../hooks/useProgramContext';
import { REQUIRED_KEYS, OPTIONAL_KEYS } from '../constants';
import { isFieldSetting, normalizeOptionalFieldSetting } from '../utils';
import type { FieldSetting } from '../types';
import type { SubjectItem, LearningObjectivesState } from '../types';
import type { AIMatchingFieldKey, AIMatchingFieldsConfig } from '../../../../../Matches/aiMatchingFields';

export function useSettingsLayoutState(view: 'configuration' | 'design', refetchKey: number) {
  const groupData = useAppSelector((state) => state.groupDetails);
  const userOptionalFields =
    groupData?.data?.userOptionalFields ?? groupData?.data?.optionalFields;
  const { groupId: grpId, orgId, location } = useProgramContext();

  const [programName, setProgramName] = useState(groupData?.data?.name ?? '');
  const [model, setModel] = useState(groupData?.data?.model ?? 'mentorship');
  const [participantLabel, setParticipantLabel] = useState(
    groupData?.data?.displayRoles?.mentee ?? ''
  );
  const [advisorLabel, setAdvisorLabel] = useState(
    groupData?.data?.displayRoles?.mentor ?? ''
  );
  const [maxMatchesPerMentee, setMaxMatchesPerMentee] = useState(
    groupData?.data?.matchesSettings?.maxMatchesPerMentee ?? 1
  );
  const [maxMatchesPerMentor, setMaxMatchesPerMentor] = useState(
    groupData?.data?.matchesSettings?.maxMatchesPerMentor ?? 1
  );
  const [sessionsPerMatch, setSessionsPerMatch] = useState(
    groupData?.data?.matchesSettings?.sessionsPerMatch ?? 1
  );
  const [useAIMatching, setUseAIMatching] = useState<boolean>(
    groupData?.data?.matchesSettings?.aiMatching?.enabled ?? false
  );
  const [aiMatchingFields, setAiMatchingFields] = useState<AIMatchingFieldsConfig>(
    (groupData?.data?.matchesSettings?.aiMatching?.fields as AIMatchingFieldsConfig | undefined) ?? {}
  );
  const [learningObjectives, setLearningObjectives] = useState<LearningObjectivesState>({
    number: 1,
    objectives: []
  });
  const [learningObjectivesLoading, setLearningObjectivesLoading] = useState(false);
  const [fieldSettings, setFieldSettings] = useState<Record<string, FieldSetting>>(
    (userOptionalFields as Record<string, FieldSetting>) ?? {}
  );
  const [subjects, setSubjects] = useState<SubjectItem[]>([]);
  const [designTemplate, setDesignTemplate] = useState<any>(null);
  const [designLoading, setDesignLoading] = useState(false);
  const [designError, setDesignError] = useState('');
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState('');
  const [isDesignDirty, setIsDesignDirty] = useState(false);

  const hasUserEditedRef = useRef(false);
  const designFetchSeqRef = useRef(0);
  const { refetch: refetchGroupDetails } = useGroupDetails(grpId, location || '');

  // Redux groupDetails can still hold the previously-visited program while
  // this route's group is being (re)fetched (ProgramDetails refetches on
  // grpId change). Deriving the template from that stale doc fetched — and
  // rendered — the WRONG program's design after "Continue". Only trust the
  // doc once it belongs to the route's group.
  const isRouteGroupData = String(groupData?.data?.grpId ?? '') === String(grpId ?? '');
  const templateId = isRouteGroupData ? groupData?.data?.goals?.templateId : undefined;
  const templateGoalName = isRouteGroupData ? groupData?.data?.goals?.goalName : undefined;
  const groupLoading = groupData?.loading;
  const groupError = groupData?.error;
  const isGroupReady = Boolean(groupData?.data);
  const basicSettingsLoading = !isGroupReady || groupLoading;
  // While the route's group hasn't landed yet, the design surface is loading —
  // otherwise DesignView reads the undefined templateId as "no template".
  const designResolving = designLoading || !isRouteGroupData;
  const isAnyLoading = basicSettingsLoading || learningObjectivesLoading || designResolving;

  const markUserEdited = useCallback(() => {
    hasUserEditedRef.current = true;
  }, []);

  // The program's name is the group's, the one its board card shows. The template's goalName
  // only fills in for a group with no name: taking it over the group's showed another title,
  // and brought the old name back after a rename.
  const groupName = isRouteGroupData ? groupData?.data?.name : undefined;
  useEffect(() => {
    if (!hasUserEditedRef.current && !groupName && templateGoalName && programName !== templateGoalName) {
      setProgramName(templateGoalName);
    }
  }, [programName, templateGoalName, groupName]);

  useEffect(() => {
    if (!groupData?.data) return;
    const availableKeys = new Set([
      ...REQUIRED_KEYS,
      ...OPTIONAL_KEYS,
      ...Object.keys(userOptionalFields || {})
    ]);
    const baseFieldSettings: Record<string, FieldSetting> = {};
    availableKeys.forEach((key) => {
      const value = userOptionalFields?.[key];
      if (value !== undefined) {
        baseFieldSettings[key] = normalizeOptionalFieldSetting(value);
      } else if (REQUIRED_KEYS.includes(key)) {
        baseFieldSettings[key] = {
          mentee: true,
          mentor: true,
          admin: true,
          mandatory: true
        };
      } else {
        const prev = fieldSettings?.[key];
        baseFieldSettings[key] = isFieldSetting(prev)
          ? prev
          : { mentee: false, mentor: false, admin: false, mandatory: false };
      }
    });
    setFieldSettings(baseFieldSettings);
    setModel(groupData.data.model ?? 'mentorship');
    const groupSubjects = groupData.data.subjects || {};
    const hydratedSubjects: SubjectItem[] = [];
    (groupSubjects.topics || []).forEach((name: string) =>
      hydratedSubjects.push({ name, type: 'topic' })
    );
    (groupSubjects.industries || []).forEach((name: string) =>
      hydratedSubjects.push({ name, type: 'industry' })
    );
    setSubjects(hydratedSubjects);
    setProgramName(groupData.data.name ?? '');
    setParticipantLabel(groupData.data.displayRoles?.mentee ?? '');
    setAdvisorLabel(groupData.data.displayRoles?.mentor ?? '');
    setMaxMatchesPerMentee(groupData.data.matchesSettings?.maxMatchesPerMentee ?? 1);
    setMaxMatchesPerMentor(groupData.data.matchesSettings?.maxMatchesPerMentor ?? 1);
    setSessionsPerMatch(groupData.data.matchesSettings?.sessionsPerMatch ?? 1);
    setUseAIMatching(groupData.data.matchesSettings?.aiMatching?.enabled ?? false);
    setAiMatchingFields(
      (groupData.data.matchesSettings?.aiMatching?.fields as AIMatchingFieldsConfig | undefined) ?? {}
    );
    hasUserEditedRef.current = false;
    setLastSavedSnapshot('');
  }, [groupData?.data, userOptionalFields]);

  const setAiMatchingField = useCallback(
    (key: AIMatchingFieldKey, value: boolean) => {
      markUserEdited();
      setAiMatchingFields((prev) => ({ ...prev, [key]: value }));
    },
    [markUserEdited]
  );

  const fetchLearningObjectives = useCallback(async () => {
    if (!grpId || !orgId) return;
    setLearningObjectivesLoading(true);
    try {
      const res = await API.getLearningObjectives({ grpId, orgId });
      const lo = res?.data?.data?.learningObjectives || {};
      const objectivesArray =
        lo?.objectives?.map((obj: any) => obj?.objective || obj)?.filter(Boolean) ?? [];
      const safeObjectives = objectivesArray.length ? objectivesArray : [''];
      setLearningObjectives({
        number: lo?.number ?? (safeObjectives.length || 1),
        objectives: safeObjectives
      });
    } catch {
      // ignore
    } finally {
      setLearningObjectivesLoading(false);
    }
  }, [grpId, orgId]);

  const fetchDesignTemplate = useCallback(async () => {
    if (!templateId || !grpId || !orgId) return;
    // Concurrent fetches can resolve out of order (e.g. the templateId flips
    // while a request is in flight); only the latest request may write state,
    // otherwise a slow earlier response overwrites the right template.
    const seq = ++designFetchSeqRef.current;
    setDesignTemplate(null);
    setDesignLoading(true);
    setDesignError('');
    try {
      const response = await API.getTemplateByTemplateId({
        templateId,
        location: location || '',
        grpId,
        orgId
      });
      if (seq !== designFetchSeqRef.current) return;
      setDesignTemplate(response?.data?.data || null);
    } catch {
      if (seq !== designFetchSeqRef.current) return;
      setDesignTemplate(null);
      setDesignError('Could not load the manual builder.');
    } finally {
      if (seq === designFetchSeqRef.current) setDesignLoading(false);
    }
  }, [grpId, location, orgId, templateId]);

  useEffect(() => {
    if (view !== 'configuration') return;
    refetchGroupDetails();
    fetchLearningObjectives();
  }, [fetchLearningObjectives, refetchGroupDetails, refetchKey, view]);

  useEffect(() => {
    if (view === 'design' && templateId) fetchDesignTemplate();
    if (view === 'design' && !templateId) setDesignTemplate(null);
    if (view !== 'design') setDesignError('');
  }, [fetchDesignTemplate, templateId, view]);

  const normalizedFieldSettings = useMemo(() => {
    const sortedKeys = Object.keys(fieldSettings || {}).sort();
    const normalized: Record<
      string,
      { mentee: boolean; mentor: boolean; admin: boolean; mandatory: boolean }
    > = {};
    sortedKeys.forEach((key) => {
      const value = fieldSettings?.[key] || {
        mentee: false,
        mentor: false,
        admin: false,
        mandatory: false
      };
      normalized[key] = {
        mentee: !!value.mentee,
        mentor: !!value.mentor,
        admin: !!value.admin,
        mandatory: !!value.mandatory
      };
    });
    return normalized;
  }, [fieldSettings]);

  const normalizedSubjects = useMemo(
    () => subjects.map((s) => ({ name: s.name, type: s.type })),
    [subjects]
  );

  const normalizedLearningObjectives = useMemo(
    () => ({
      number: learningObjectives.number,
      objectives: [...(learningObjectives.objectives || [])]
    }),
    [learningObjectives]
  );

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        programName,
        model,
        participantLabel,
        advisorLabel,
        maxMatchesPerMentee,
        maxMatchesPerMentor,
        sessionsPerMatch,
        useAIMatching,
        aiMatchingFields,
        fieldSettings: normalizedFieldSettings,
        subjects: normalizedSubjects,
        learningObjectives: normalizedLearningObjectives
      }),
    [
      programName,
      model,
      participantLabel,
      advisorLabel,
      maxMatchesPerMentee,
      maxMatchesPerMentor,
      sessionsPerMatch,
      useAIMatching,
      aiMatchingFields,
      normalizedFieldSettings,
      normalizedSubjects,
      normalizedLearningObjectives
    ]
  );

  useEffect(() => {
    if (!hasUserEditedRef.current && lastSavedSnapshot !== currentSnapshot) {
      setLastSavedSnapshot(currentSnapshot);
    }
  }, [currentSnapshot, lastSavedSnapshot]);

  const settingsDirty =
    lastSavedSnapshot !== '' && currentSnapshot !== lastSavedSnapshot;
  const hasPendingChanges = settingsDirty || isDesignDirty;

  const updateField = useCallback(
    (fieldKey: string, role: 'mentee' | 'mentor' | 'admin' | 'mandatory', value: boolean) => {
      markUserEdited();
      setFieldSettings((prev) => ({
        ...prev,
        [fieldKey]: { ...prev[fieldKey], [role]: value }
      }));
    },
    [markUserEdited]
  );

  return {
    grpId,
    orgId,
    location,
    view,
    groupData,
    programName,
    setProgramName,
    model,
    setModel,
    participantLabel,
    setParticipantLabel,
    advisorLabel,
    setAdvisorLabel,
    maxMatchesPerMentee,
    setMaxMatchesPerMentee,
    maxMatchesPerMentor,
    setMaxMatchesPerMentor,
    sessionsPerMatch,
    setSessionsPerMatch,
    useAIMatching,
    setUseAIMatching,
    aiMatchingFields,
    setAiMatchingField,
    learningObjectives,
    setLearningObjectives,
    fieldSettings,
    subjects,
    setSubjects,
    designTemplate,
    designLoading: designResolving,
    designError,
    templateId,
    templateGoalName,
    basicSettingsLoading,
    learningObjectivesLoading,
    groupError,
    isAnyLoading,
    hasPendingChanges,
    currentSnapshot,
    lastSavedSnapshot,
    setLastSavedSnapshot,
    isDesignDirty,
    setIsDesignDirty,
    markUserEdited,
    updateField,
    refetchGroupDetails,
    fetchLearningObjectives
  };
}
